import { Injectable, inject } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CmsApiService } from '../api/cms-api.service';
import { UserService } from '../user/user.service';
import { UpdateOnLangChange } from '../../shared/decorators/update-on-lang-change';
import { ArrayService } from '../array.service';
import { GameAttribute } from './game-attribute';
import { GameCategory } from './game-category';
import { GameAttrType, GameCategoryType } from '../../shared/interfaces/game-category-interface';

/**
 * These categories will not be displayed on the front
 */
const NOT_DISPLAY_CATEGORIES = ['thank-you'];

@Injectable({
  providedIn: 'root',
})
export class GameCategoryAttrService {
  private _api = inject(CmsApiService);
  private _user = inject(UserService);
  private _array = inject(ArrayService);


  private _orderCategoryAttrList = [
    GameAttribute.NEW,
    GameAttribute.DAILY_DROPS,
    GameCategory.BONUS_BUY,
    GameAttribute.JACKPOT,
    GameAttribute.MEGAWAYS,
    GameCategory.LIVE_CASINO,
    GameAttribute.LIVE_BLACKJACK,
    GameCategory.TABLE_GAMES,
    GameCategory.ROULETTE,
    GameCategory.VIDEO_POKER,
  ];

  private _filteredCategoryAttrList$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * Game category list observable
   */
  private _categoryList$: ReplaySubject<Array<GameCategoryType>> = new ReplaySubject<Array<GameCategoryType>>(1);

  /**
   * Game category list
   */
  private _categoryList: Array<GameCategoryType> = [];

  /**
   * Game attr list observable
   */
  private _attrList$: ReplaySubject<Array<GameAttrType>> = new ReplaySubject<Array<GameAttrType>>(1);

  /**
   * Game attr list
   */
  private _attrList: Array<GameAttrType> = [];

  /**
   * Game category & attr list observable
   */
  private _categoryAttrList$: ReplaySubject<{
    attributeList: GameAttrType[],
    categoryList: GameCategoryType[]
  }> = new ReplaySubject<{
    attributeList: GameAttrType[],
    categoryList: GameCategoryType[]
  }>(1);

  constructor() {
    this.updateCategoryAttrList().subscribe();
  }

  get filteredCategoryAttrList$(): any {
    return this._filteredCategoryAttrList$;
  }

  /**
   * Access to category list observable from outside
   */
  get categoryList$(): ReplaySubject<Array<GameCategoryType>> {
    return this._categoryList$;
  }

  /**
   * Access to category list from outside
   */
  get categoryList(): Array<GameCategoryType> {
    return this._categoryList;
  }

  /**
   * Access to attr list observable from outside
   */
  get attrList$(): ReplaySubject<Array<GameAttrType>> {
    return this._attrList$;
  }

  /**
   * Access to attr list from outside
   */
  get attrList(): Array<GameAttrType> {
    return this._attrList;
  }

  /**
   * Access to category & attr list observable from outside
   */
  get categoryAttrList$(): ReplaySubject<{ attributeList: GameAttrType[], categoryList: GameCategoryType[] }> {
    return this._categoryAttrList$;
  }

  /**
   * Returns attr by id
   *
   * @param id
   */
  getAttrById(id): GameAttrType {
    return this._attrList.find(attr => attr.id === +id);
  }

  /**
   * Returns attr by name
   *
   * @param name
   */
  getAttrByAlias(name: string): GameAttrType {
    name = name.toLowerCase().replace(' ', '-');
    return this._attrList.find(attr => attr.slug === name);
  }


  /**
   * Get category & attr list from CMS and save to local variables
   */
  @UpdateOnLangChange('game/category/attribute/list')
  updateCategoryAttrList(): ReplaySubject<any> {
    const params = this._user.auth ? { country: this._user.info.country } : {};

    return this._api.gameCategoryAttrList(params).pipe(
      map(response => response.data),
      map((data: { categoryList: GameCategoryType[], attributeList: GameAttrType[] }) => {

        const categoryList = data.categoryList.map(category => ({
          ...category,
          id: +category.id,
        }));

        const attributeList = data.attributeList.map(attr => ({
          ...attr,
          id: +attr.id,
          slug: attr.short,
        }));

        return { categoryList, attributeList };
      }),
      tap((data: { categoryList: GameCategoryType[], attributeList: GameAttrType[] }) => {

        const sortedCategoryList = data.categoryList
          .filter(c => !NOT_DISPLAY_CATEGORIES.includes(c.slug))
          .sort((a, b) => a.name.localeCompare(b.name));

        const sortedAttributeList = data.attributeList
          .sort((a, b) => a.name.localeCompare(b.name));

        this._categoryList = sortedCategoryList;
        this._attrList = sortedAttributeList;

        this._categoryList$.next(this._categoryList);
        this._attrList$.next(this._attrList);
        this._categoryAttrList$.next({ attributeList: this._attrList, categoryList: this._categoryList });

        if (this._user.auth) {
          this._orderCategoryAttrList.unshift(GameCategory.LAST_PLAYED_ID);
        }

        const filteredCategories = sortedCategoryList.filter(category =>
          this._orderCategoryAttrList.includes(category.id),
        );

        const filteredAttributes = sortedAttributeList.filter(attribute =>
          this._orderCategoryAttrList.includes(attribute.id),
        );

        this._filteredCategoryAttrList$.next(
          this._array.mapOrder([
            ...filteredAttributes,
            ...filteredCategories,
          ], this._orderCategoryAttrList, 'id'),
        );
      }),
    ) as ReplaySubject<any>;
  }

}
